import React from "react";
import {
  EconomyCompareResult,
  SimulationType,
  SIMULATION_LABELS,
  ECONOMY_CARD_LABELS,
  IResponseCativo,
  IResponseGD,
  EconomyTargetPrice,
} from "../types";
import { formatBrazilianCurrency } from "../../../utils/utilityFunctions";

interface SimulatorTableProps {
  simulationResult: EconomyCompareResult;
  simulationType: SimulationType;
}

const fmtVol = (value: number, unit: string) =>
  `${value.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} ${unit}`;

type DetailRowDefinition = {
  label: string;
  unit?: string;
  volume?: (sim: any) => number;
  cost: (sim: any) => number;
};

const commonUtilityRows: DetailRowDefinition[] = [
  {
    label: "Demanda Contratada FP",
    unit: "kW",
    volume: (sim) => sim.demandaForaPonta.volume,
    cost: (sim) => sim.demandaForaPonta.custoTotal.comImposto,
  },
  {
    label: "Demanda Contratada P",
    unit: "kW",
    volume: (sim) => sim.demandaPonta.volume,
    cost: (sim) => sim.demandaPonta.custoTotal.comImposto,
  },
  {
    label: "Demanda Ultrapassagem FP",
    unit: "kW",
    volume: (sim) => sim.demandaForaPontaUltrapassagem.volume,
    cost: (sim) => sim.demandaForaPontaUltrapassagem.custoTotal.comImposto,
  },
  {
    label: "Demanda Ultrapassagem P",
    unit: "kW",
    volume: (sim) => sim.demandaPontaUltrapassagem.volume,
    cost: (sim) => sim.demandaPontaUltrapassagem.custoTotal.comImposto,
  },
  {
    label: "TUSD FP",
    unit: "kWh",
    volume: (sim) => sim.consumoForaPonta.volume,
    cost: (sim) => sim.consumoForaPonta.custoTotal.comImposto,
  },
  {
    label: "TUSD P",
    unit: "kWh",
    volume: (sim) => sim.consumoPonta.volume,
    cost: (sim) => sim.consumoPonta.custoTotal.comImposto,
  },
  {
    label: "TE FP",
    unit: "kWh",
    volume: (sim) => sim.energiaForaPonta.volume,
    cost: (sim) => sim.energiaForaPonta.custoTotal.comImposto,
  },
  {
    label: "TE P",
    unit: "kWh",
    volume: (sim) => sim.energiaPonta.volume,
    cost: (sim) => sim.energiaPonta.custoTotal.comImposto,
  },
  {
    label: "Gerador",
    unit: "kWh",
    volume: (sim) => sim.custoGerador.volume,
    cost: (sim) => sim.custoGerador.custoTotal.comImposto,
  },
];

const cativoRows = commonUtilityRows;

const gdRows: DetailRowDefinition[] = [
  ...commonUtilityRows.slice(0, 4),
  {
    label: "Demanda Usina",
    unit: "kW",
    volume: (sim) => sim.demandaUsina.volume,
    cost: (sim) => sim.demandaUsina.custoTotal.comImposto,
  },
  ...commonUtilityRows.slice(4, 8),
  {
    label: "Energia injetada TUSD FP",
    unit: "kWh",
    volume: (sim) => sim.consumoForaPontaInjetada.volume,
    cost: (sim) => sim.consumoForaPontaInjetada.custoTotal.comImposto,
  },
  {
    label: "Energia injetada TUSD P",
    unit: "kWh",
    volume: (sim) => sim.consumoPontaInjetada.volume,
    cost: (sim) => sim.consumoPontaInjetada.custoTotal.comImposto,
  },
  {
    label: "Energia injetada TE FP",
    unit: "kWh",
    volume: (sim) => sim.energiaForaPontaInjetada.volume,
    cost: (sim) => sim.energiaForaPontaInjetada.custoTotal.comImposto,
  },
  {
    label: "Energia injetada TE P",
    unit: "kWh",
    volume: (sim) => sim.energiaPontaInjetada.volume,
    cost: (sim) => sim.energiaPontaInjetada.custoTotal.comImposto,
  },
  commonUtilityRows[8],
];

const mlRows: DetailRowDefinition[] = [
  ...commonUtilityRows.slice(0, 2),
  ...commonUtilityRows.slice(4, 6),
  {
    label: "Total energia ACL",
    unit: "kWh",
    volume: (sim) => sim.consumoForaPonta.volume + sim.consumoPonta.volume,
    cost: (sim) => sim.custoEnergia.custoTotal.comImposto,
  },
  {
    label: "Benefício tarifário",
    cost: (sim) => sim.beneficioTarifario.custos.custoTotal.comImposto,
  },
  {
    label: "Encargos",
    cost: (sim) => sim.encargos.custos.custoTotal.comImposto,
  },
  {
    label: "Custo Operacional e CCEE",
    cost: (sim) => sim.operacional,
  },
];

const apeRows: DetailRowDefinition[] = [
  ...commonUtilityRows.slice(0, 2),
  {
    label: "Demanda usina",
    unit: "kW",
    volume: (sim) => sim.demandaUsina.volume,
    cost: (sim) => sim.demandaUsina.custoTotal.comImposto,
  },
  ...commonUtilityRows.slice(4, 6),
  {
    label: "TUSD FP APE",
    unit: "kWh",
    volume: (sim) => sim.consumoForaPontaAGP.volume,
    cost: (sim) => sim.consumoForaPontaAGP.custoTotal.comImposto,
  },
  {
    label: "TUSD P APE",
    unit: "kWh",
    volume: (sim) => sim.consumoPontaAGP.volume,
    cost: (sim) => sim.consumoPontaAGP.custoTotal.comImposto,
  },
  {
    label: "Energia complementar",
    unit: "kWh",
    volume: (sim) => sim.energiaFaturavel.volume,
    cost: (sim) => sim.energiaFaturavel.custoTotal.comImposto,
  },
  {
    label: "Energia excedente",
    unit: "kWh",
    volume: (sim) => sim.energiaExcedente.volume,
    cost: (sim) => sim.energiaExcedente.custoTotal.comImposto,
  },
  {
    label: "Benefício tarifário",
    cost: (sim) => sim.beneficioTarifario.custos.custoTotal.comImposto,
  },
  {
    label: "Encargos",
    cost: (sim) => sim.encargos.custos.custoTotal.comImposto,
  },
  {
    label: "Custo Operacional e CCEE",
    cost: (sim) => sim.operacional,
  },
];

const FIRST_SIMULATION_ROWS: Record<SimulationType, DetailRowDefinition[]> = {
  ML: cativoRows,
  APE: cativoRows,
  MLXAPE: mlRows,
  GDXAPE: gdRows,
  MLBESS: mlRows,
  APEBESS: apeRows,
};

const SECOND_SIMULATION_ROWS: Record<SimulationType, DetailRowDefinition[]> = {
  ML: mlRows,
  APE: apeRows,
  MLXAPE: apeRows,
  GDXAPE: apeRows,
  MLBESS: mlRows,
  APEBESS: apeRows,
};

function Row({
  label,
  volume,
  unit,
  cost,
}: {
  label: string;
  volume?: number;
  unit?: string;
  cost: number;
}) {
  return (
    <div className="ResSimulTableTitle">
      <p className="ResSimulTableContent" style={{ textAlign: "left" }}>
        {label}
      </p>
      <p className="ResSimulTableContentC">
        {volume !== undefined && unit ? fmtVol(volume, unit) : "-"}
      </p>
      <div className="separarNum">
        <p className="ResSimulTableContentB">{formatBrazilianCurrency(cost)}</p>
      </div>
    </div>
  );
}

function BlankRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div className="ResSimulTableTitle" key={index}>
          <p className="ResSimulTableContent" style={{ textAlign: "left" }} />
          <p
            className="ResSimulTableContentD"
            style={{ textAlign: "center", lineHeight: "34px" }}
          />
          <div className="separarNum">
            <p className="ResSimulTableContentB" />
          </div>
        </div>
      ))}
    </>
  );
}

function TableHeader() {
  return (
    <div className="ResSimulTableTitle">
      <p className="ResSimulTable" style={{ textAlign: "left" }}>
        Grandeza Elétrica
      </p>
      <p className="ResSimulTable">Grandezas Faturadas</p>
      <p className="ResSimulTable">Faturado</p>
    </div>
  );
}

function TableTotal({ total }: { total: number }) {
  return (
    <>
      <hr />
      <div className="ResSimulTableTitle">
        <p
          className="ResSimulTableContent"
          style={{ textAlign: "left", fontWeight: "bold", fontSize: "16px" }}
        >
          Total
        </p>
        <p className="ResSimulTableContent" />
        <div className="separarNum">
          <p
            className="ResSimulTableContentB"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              fontWeight: "bold",
              fontSize: "16px",
            }}
          >
            {formatBrazilianCurrency(total)}
          </p>
        </div>
      </div>
    </>
  );
}

function DetailRows({ sim, rows }: { sim: any; rows: DetailRowDefinition[] }) {
  return (
    <>
      {rows.map(({ label, unit, volume, cost }) => (
        <Row
          key={label}
          label={label}
          volume={volume?.(sim)}
          unit={unit}
          cost={cost(sim)}
        />
      ))}
    </>
  );
}

function FirstSimulationRows({
  simulationType,
  firstSimulation,
}: {
  simulationType: SimulationType;
  firstSimulation: EconomyCompareResult["firstSimulation"];
}) {
  return (
    <DetailRows
      sim={firstSimulation}
      rows={FIRST_SIMULATION_ROWS[simulationType]}
    />
  );
}

function SecondSimulationRows({
  simulationType,
  secondSimulation,
}: {
  simulationType: SimulationType;
  secondSimulation: EconomyCompareResult["secondSimulation"];
}) {
  return (
    <DetailRows
      sim={secondSimulation}
      rows={SECOND_SIMULATION_ROWS[simulationType]}
    />
  );
}

function BandeirasSection({
  firstSimulation,
  secondSimulation,
  simulationType,
  targetPrices,
}: {
  firstSimulation: EconomyCompareResult["firstSimulation"];
  secondSimulation: EconomyCompareResult["secondSimulation"];
  simulationType: SimulationType;
  targetPrices: EconomyTargetPrice[] | null;
}) {
  if (
    simulationType !== "ML" &&
    simulationType !== "APE" &&
    simulationType !== "GDXAPE"
  ) {
    return null;
  }

  const simulationWithFlags = firstSimulation as IResponseCativo | IResponseGD;
  const { valoresBandeiras } = simulationWithFlags;
  const secondTotal = secondSimulation.custos.custoTotal.comImposto;
  const firstCostLabel =
    simulationType === "GDXAPE" ? "Custo GD" : "Custo cativo";
  const secondCostLabel =
    simulationType === "GDXAPE" ? "Custo APE" : "Custo Livre";

  const bands = [
    {
      label: "Verde",
      cost: valoresBandeiras.custoTotal.verde,
      cls: "bandeiraVerde",
    },
    {
      label: "Amarela",
      cost: valoresBandeiras.custoTotal.amarela,
      cls: "bandeiraAmarela",
    },
    {
      label: "Vermelha P1",
      cost: valoresBandeiras.custoTotal.vermelhaP1,
      cls: "bandeiraVermelhaP1",
    },
    {
      label: "Vermelha P2",
      cost: valoresBandeiras.custoTotal.vermelhaP2,
      cls: "bandeiraVermelhaP2",
    },
  ];

  if (targetPrices?.length) {
    return (
      <div className="containerResSimulMaster" style={{ marginTop: "16px" }}>
        <div className="containerResSimul">
          <p className="containerResSimulTitle">Bandeiras tarifárias</p>

          <div className="ResSimulTableTitle">
            <p className="ResSimulTable">Bandeira</p>
            <p className="ResSimulTable">{firstCostLabel}</p>
            <p className="ResSimulTable" style={{ width: "144px" }}>
              Economia
            </p>
          </div>

          {bands.map(({ label, cost, cls }) => {
            const economia = cost - secondTotal;
            const economiaPct = cost > 0 ? economia / cost : 0;

            return (
              <div className="ResSimulTableTitle" key={label}>
                <div className="ResSimulTableContent">
                  {label}
                  <div className={cls} />
                </div>

                <p className="ResSimulTableContentC">
                  {formatBrazilianCurrency(cost)}
                </p>

                <div>
                  <p
                    style={{
                      width: "144px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                    className="ResSimulTableContentB"
                  >
                    <span style={{ fontWeight: "bold" }}>
                      {formatBrazilianCurrency(economia)}
                    </span>

                    <span
                      style={{ fontWeight: "bold" }}
                      className={
                        economia >= 0
                          ? "economia-positivo"
                          : "economia-negativo"
                      }
                    >
                      {economiaPct.toLocaleString("pt-BR", {
                        style: "percent",
                      })}
                    </span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="containerResSimul">
          <p className="containerResSimulTitle">
            Comparação de preços de energia
          </p>

          <div style={{ padding: "0 32px" }}>
            <div className="ResSimulTableTitle">
              <p className="ResSimulTable" style={{ textAlign: "left" }}>
                Preço de energia
              </p>
              <p className="ResSimulTable" style={{ width: "144px" }}>
                Economia
              </p>
            </div>

            {targetPrices.map((targetPrice, index) => (
              <React.Fragment key={`${targetPrice.precoEnergia}-${index}`}>
                <div className="ResSimulTableTitle">
                  <p
                    className="ResSimulTableContent"
                    style={{ textAlign: "left", margin: "4px 0" }}
                  >
                    {formatBrazilianCurrency(targetPrice.precoEnergia)}
                  </p>

                  <div>
                    <p
                      style={{
                        width: "144px",
                        display: "flex",
                        justifyContent: "space-between",
                        margin: "4px 0",
                      }}
                      className="ResSimulTableContentB"
                    >
                      <span style={{ fontWeight: "bold" }}>
                        {formatBrazilianCurrency(targetPrice.economia)}
                      </span>

                      <span
                        style={{ fontWeight: "bold" }}
                        className={
                          targetPrice.economiaPct >= 0
                            ? "economia-positivo"
                            : "economia-negativo"
                        }
                      >
                        {targetPrice.economiaPct.toLocaleString("pt-BR", {
                          style: "percent",
                        })}
                      </span>
                    </p>
                  </div>
                </div>

                {index === 0 && (
                  <div
                    style={{
                      border: 0,
                      borderTop: "1px solid #ccc",
                      height: 0,
                      width: "100%",
                      margin: 0,
                      padding: 0,
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ marginTop: "16px" }}>
        <span className="simulacao-form-header">Bandeiras</span>
        <hr />
      </div>

      <div className="resultadoSimulacaoContainer">
        {bands.map(({ label, cost, cls }) => {
          const economia = cost - secondTotal;
          const economiaPct = cost > 0 ? economia / cost : 0;
          return (
            <React.Fragment key={label}>
              <div className="bandeirasDiv">
                <span className="bandeirasTitle">{firstCostLabel}</span>
                <div className="economia-card">
                  <h1
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    {label}
                    <span style={{ fontWeight: "bold" }}>
                      {formatBrazilianCurrency(cost)}
                    </span>
                  </h1>
                  <div className={cls} />
                </div>
              </div>

              <div className="bandeirasDiv">
                <span className="bandeirasTitle">{secondCostLabel}</span>
                <div className="economia-card">
                  <h1
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    {simulationType === "GDXAPE" ? "APE" : "Livre"}
                    <span style={{ fontWeight: "bold" }}>
                      {formatBrazilianCurrency(secondTotal)}
                    </span>
                  </h1>
                  <div className="bandeiraAzul" />
                </div>
              </div>

              <div className="bandeirasDiv">
                <span className="bandeirasTitle">Economia Mensal</span>
                <div className="economia-card">
                  <h1
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span style={{ fontWeight: "bold" }}>
                      {formatBrazilianCurrency(economia)}
                    </span>
                    <span
                      style={{ fontWeight: "bold" }}
                      className={
                        economia >= 0
                          ? "economia-positivo"
                          : "economia-negativo"
                      }
                    >
                      {economiaPct.toLocaleString("pt-BR", {
                        style: "percent",
                      })}
                    </span>
                  </h1>
                  <div className="bandeiraCinza" />
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
}

export function SimulatorTable({
  simulationResult,
  simulationType,
}: SimulatorTableProps) {
  const labels = SIMULATION_LABELS[simulationType];
  const economyCardLabels = ECONOMY_CARD_LABELS[simulationType];
  const { firstSimulation, secondSimulation, targetPrices } = simulationResult;
  const firstTotal = firstSimulation.custos.custoTotal.comImposto;
  const secondTotal = secondSimulation.custos.custoTotal.comImposto;
  const economia = firstTotal - secondTotal;
  const economiaPct = firstTotal > 0 ? economia / firstTotal : 0;
  const firstRows = FIRST_SIMULATION_ROWS[simulationType];
  const secondRows = SECOND_SIMULATION_ROWS[simulationType];
  const maxDetailRows = Math.max(firstRows.length, secondRows.length);
  const firstBlankRows = maxDetailRows - firstRows.length;
  const secondBlankRows = maxDetailRows - secondRows.length;

  return (
    <>
      <div className="containerResSimulMaster">
        {/* First simulation column */}
        <div className="containerResSimul">
          <p className="containerResSimulTitle">{labels.first}</p>
          <TableHeader />
          <FirstSimulationRows
            simulationType={simulationType}
            firstSimulation={firstSimulation}
          />
          <BlankRows count={firstBlankRows} />
          <TableTotal total={firstTotal} />
        </div>

        {/* Second simulation column */}
        <div className="containerResSimul">
          <p className="containerResSimulTitle">{labels.second}</p>
          <TableHeader />
          <SecondSimulationRows
            simulationType={simulationType}
            secondSimulation={secondSimulation}
          />
          <BlankRows count={secondBlankRows} />
          <TableTotal total={secondTotal} />
        </div>
      </div>

      {/* Monthly economy summary */}
      <div className="resultadoSimulacaoContainer">
        <div className="economia-card">
          <h1>{economyCardLabels.first}</h1>
          <h2>{formatBrazilianCurrency(firstTotal)}</h2>
        </div>

        <div className="economia-card">
          <h1>{economyCardLabels.second}</h1>
          <h2>{formatBrazilianCurrency(secondTotal)}</h2>
        </div>

        <div className="economia-card">
          <h1>Economia mensal</h1>
          <div className="economia-value">
            <h2>{formatBrazilianCurrency(economia)}</h2>
            <h3
              className={
                economia >= 0 ? "economia-positivo" : "economia-negativo"
              }
            >
              {economiaPct.toLocaleString("pt-BR", { style: "percent" })}
            </h3>
          </div>
        </div>
      </div>

      <BandeirasSection
        firstSimulation={firstSimulation}
        secondSimulation={secondSimulation}
        simulationType={simulationType}
        targetPrices={targetPrices}
      />
    </>
  );
}
